import { useState, useCallback } from "react";
import { EmailRecord } from "@/types/email";
import { toast } from "sonner";
import { useEmailStorage } from "./emails/useEmailStorage";
import { calculateEmailStats } from "./emails/useEmailStats";
import { Investor, Contact } from "@/types/investor";

export const useEmailDraftsManagement = () => {
  const [drafts, setDrafts] = useState<EmailRecord[]>([]);
  const [selectedDraft, setSelectedDraft] = useState<EmailRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  
  const { loadDraftsFromStorage, updateEmailRecord, deleteEmailRecord, markDraftAsSent } = useEmailStorage();

  // Load drafts from storage
  const loadDrafts = useCallback(async () => {
    if (dataFetched) return drafts;
    
    setIsLoading(true);
    try {
      const loadedDrafts = await loadDraftsFromStorage();
      setDrafts(loadedDrafts);
      setDataFetched(true);
      
      return loadedDrafts;
    } catch (error) {
      console.error("Error loading draft emails:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [loadDraftsFromStorage, dataFetched, drafts]);

  // View a draft
  const handleViewDraft = (draft: EmailRecord) => {
    setSelectedDraft(draft);
  };

  // Close draft details
  const handleCloseDraftDetails = () => {
    setSelectedDraft(null);
  };

  // Update draft
  const handleDraftUpdated = async (updatedDraft: EmailRecord) => {
    try {
      setIsLoading(true);
      const updatedDrafts = await updateEmailRecord(updatedDraft);
      
      // Only keep drafts from the updated emails
      const filteredDrafts = updatedDrafts.filter(email => email.status === "draft");
      setDrafts(filteredDrafts);
      setSelectedDraft(updatedDraft);
      
      toast.success("Draft updated successfully");
      return filteredDrafts;
    } catch (error) {
      console.error("Error updating draft:", error);
      toast.error("Failed to update draft");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Delete draft
  const handleDeleteDraft = async (draftToDelete: EmailRecord) => {
    try {
      setIsLoading(true);
      const updatedEmails = await deleteEmailRecord(draftToDelete.id);
      
      // Only keep drafts from the updated emails
      const filteredDrafts = updatedEmails.filter(email => email.status === "draft");
      setDrafts(filteredDrafts);
      
      // If we're currently viewing this draft, close the detail view
      if (selectedDraft && selectedDraft.id === draftToDelete.id) {
        setSelectedDraft(null);
      }
      
      toast.success("Draft deleted successfully");
      return filteredDrafts;
    } catch (error) {
      console.error("Error deleting draft:", error);
      toast.error("Failed to delete draft");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Mark draft as sent
  const handleMarkAsSent = async (draft: EmailRecord) => {
    try {
      setIsLoading(true);
      const sentEmail = {
        ...draft,
        status: "sent",
        sentAt: new Date().toISOString(),
        hasResponse: false
      } as EmailRecord;
      
      const updatedEmails = await markDraftAsSent(sentEmail);
      
      // Only keep drafts from the updated emails
      const filteredDrafts = updatedEmails.filter(email => email.status === "draft");
      setDrafts(filteredDrafts);
      
      // Close the detail view since it's no longer a draft
      if (selectedDraft && selectedDraft.id === draft.id) {
        setSelectedDraft(null);
      }
      
      toast.success("Email marked as sent and moved to Sent Emails");
      return filteredDrafts;
    } catch (error) {
      console.error("Error marking draft as sent:", error);
      toast.error("Failed to mark draft as sent");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    drafts,
    selectedDraft,
    isLoading,
    loadDrafts,
    handleViewDraft,
    handleCloseDraftDetails,
    handleDraftUpdated,
    handleDeleteDraft,
    handleMarkAsSent
  };
};
